/**
 * Algolia Search Experimentation Tracker
 *
 * Tracks search personalization A/B test events for analysis and reporting.
 * Integrates with Algolia Insights API and Adobe Customer Journey Analytics.
 *
 *   algolia:experiment:search      -> algolia_experiment_search
 *   algolia:experiment:click       -> algolia_experiment_click
 *   algolia:experiment:conversion  -> algolia_experiment_conversion
 *   algolia:experiment:purchase    -> algolia_experiment_purchase
 *
 * @module AlgoliaExperimentTracker
 */
(function () {
  'use strict';

  /**
   * Resolve the A/B assignment at call time, not at load time.
   * A copy cached when this file loads stays null forever if the AB test
   * manager assigns the shopper later (or loads after this file), and every
   * event would then be dropped silently.
   * @returns {Object|null}
   */
  function resolveTestContext() {
    const manager = window.AlgoliaABTestManager;
    if (!manager || typeof manager.getTestContext !== 'function') return null;
    return manager.getTestContext() || null;
  }

  /**
   * Algolia userToken for the current shopper (from the Insights helper), if available
   * @returns {string|undefined}
   */
  function resolveUserToken() {
    try {
      const insights = window.AlgoliaInsights;
      if (insights && typeof insights.getUserToken === 'function') {
        return insights.getUserToken() || undefined;
      }
    } catch {
      // Silently ignore errors when getUserToken is not available
    }
    return undefined;
  }

  // Constants
  const EVENT_DEDUPE_WINDOW_MS = 2000;
  const recentEvents = new Map();

  /**
   * Check if event is duplicate (within dedupe window)
   * @param {string} key - Event deduplication key
   * @returns {boolean}
   */
  function isDuplicateEvent(key) {
    const now = Date.now();
    // Clean up expired entries
    recentEvents.forEach((timestamp, eventKey) => {
      if (now - timestamp > EVENT_DEDUPE_WINDOW_MS) {
        recentEvents.delete(eventKey);
      }
    });

    if (recentEvents.has(key)) {
      return true;
    }
    recentEvents.set(key, now);
    return false;
  }

  /**
   * Dispatch custom event for analytics layer to consume
   * Follows standard pattern: theme code dispatches events -> analytics snippet publishes to Shopify
   * @param {string} eventName - Custom event name
   * @param {Object} eventData - Event data
   */
  function dispatchAnalyticsEvent(eventName, eventData) {
    try {
      const event = new CustomEvent(eventName, {
        detail: eventData,
        bubbles: true,
        cancelable: false,
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('[Experiment Tracker] Failed to dispatch event:', eventName, error);
    }
  }

  /**
   * Experiment Tracker Class
   */
  class ExperimentTracker {
    constructor() {
      this.init();
    }

    /**
     * Initialize tracker
     */
    init() {
      if (!resolveTestContext()) {
        console.warn('[Experiment Tracker] No active test assignment yet; it is read again on every event');
      }
    }

    /**
     * Track search results view with test assignment
     * @param {Object} params - Search parameters
     */
    trackSearchResults(params) {
      const { queryID, indexName, query, resultsCount, filters = {}, sort = 'default', page = 1 } = params;

      const context = resolveTestContext();
      if (!queryID || !context) {
        console.warn('[Experiment Tracker] Search event dropped:', !queryID ? 'missing queryID' : 'no test assignment');
        return;
      }

      // Create deduplication key
      const eventKey = ['search', queryID, context.sessionId].join('|');
      if (isDuplicateEvent(eventKey)) {
        return;
      }

      // Dispatch custom event (consumed by algolia-experiment-integration.liquid)
      dispatchAnalyticsEvent('algolia:experiment:search', {
        eventType: 'search_results_viewed',
        queryID: queryID,
        indexName: indexName,
        query: query,
        resultsCount: resultsCount,
        filters: filters,
        sort: sort,
        page: page,
        userToken: resolveUserToken(),
        testVariant: context.variant,
        testVariantName: context.variantName,
        testSessionId: context.sessionId,
      });
    }

    /**
     * Track product click from search results
     * @param {Object} params - Click parameters
     */
    trackProductClick(params) {
      const { queryID, indexName, objectID, position, productHandle, productTitle } = params;

      const context = resolveTestContext();
      if (!queryID || !objectID || !context) {
        console.warn(
          '[Experiment Tracker] Click event dropped:',
          !context ? 'no test assignment' : 'missing queryID/objectID',
        );
        return;
      }

      // Create deduplication key
      const eventKey = ['click', queryID, objectID, position].join('|');
      if (isDuplicateEvent(eventKey)) {
        return;
      }

      // Algolia Insights click event is handled by algolia-insights-utils.js
      // This tracker only dispatches to analytics layer

      dispatchAnalyticsEvent('algolia:experiment:click', {
        eventType: 'search_result_clicked',
        queryID: queryID,
        indexName: indexName,
        objectID: objectID,
        position: position,
        productHandle: productHandle,
        productTitle: productTitle,
        userToken: resolveUserToken(),
        testVariant: context.variant,
        testVariantName: context.variantName,
        testSessionId: context.sessionId,
      });
    }

    /**
     * Track add-to-cart conversion
     * @param {Object} params - Conversion parameters
     */
    trackAddToCart(params) {
      const {
        queryID,
        indexName,
        objectID,
        productId,
        productHandle,
        productTitle,
        variantId,
        quantity = 1,
        price,
      } = params;

      const context = resolveTestContext();
      if (!objectID || !context) {
        console.warn(
          '[Experiment Tracker] Add-to-cart event dropped:',
          !context ? 'no test assignment' : 'missing objectID',
        );
        return;
      }

      // Create deduplication key
      const eventKey = ['atc', objectID, variantId, context.sessionId].join('|');
      if (isDuplicateEvent(eventKey)) {
        return;
      }

      // Dispatch custom event for analytics layer
      dispatchAnalyticsEvent('algolia:experiment:conversion', {
        eventType: 'add_to_cart',
        queryID: queryID,
        indexName: indexName,
        objectID: objectID,
        productId: productId,
        productHandle: productHandle,
        productTitle: productTitle,
        variantId: variantId,
        quantity: quantity,
        price: price,
        userToken: resolveUserToken(),
        testVariant: context.variant,
        testVariantName: context.variantName,
        testSessionId: context.sessionId,
      });

      // Also send to Algolia Insights if queryID available
      if (queryID && window.AlgoliaInsights && typeof window.AlgoliaInsights.publishConversion === 'function') {
        window.AlgoliaInsights.publishConversion('instantsearch', indexName, objectID, queryID);
      }
    }

    /**
     * Track purchase completion
     * @param {Object} params - Purchase parameters
     */
    trackPurchase(params) {
      const { orderId, revenue, items = [], queryID } = params;

      const context = resolveTestContext();
      if (!context || !orderId) {
        console.warn(
          '[Experiment Tracker] Purchase event dropped:',
          !context ? 'no test assignment' : 'missing orderId',
        );
        return;
      }

      // Create deduplication key
      const eventKey = ['purchase', orderId, context.sessionId].join('|');
      if (isDuplicateEvent(eventKey)) {
        return;
      }

      // Dispatch custom event for analytics layer
      dispatchAnalyticsEvent('algolia:experiment:purchase', {
        eventType: 'purchase',
        orderId: orderId,
        revenue: revenue,
        items: items,
        queryID: queryID,
        userToken: resolveUserToken(),
        testVariant: context.variant,
        testVariantName: context.variantName,
        testSessionId: context.sessionId,
      });
    }

    /**
     * Get current test context
     * @returns {Object|null}
     */
    getTestContext() {
      return resolveTestContext();
    }
  }

  // Initialize global instance
  const tracker = new ExperimentTracker();

  // Expose public API
  window.AlgoliaExperimentTracker = {
    trackSearchResults: (params) => tracker.trackSearchResults(params),
    trackProductClick: (params) => tracker.trackProductClick(params),
    trackAddToCart: (params) => tracker.trackAddToCart(params),
    trackPurchase: (params) => tracker.trackPurchase(params),
    getTestContext: () => tracker.getTestContext(),
    // Internal access
    _instance: tracker,
  };
})();
